import React, { useState } from 'react';
import { Package } from 'lucide-react';
import { getUnitLabel, getUnitPrice } from '../../utils/formatStock';

const UNITS = ['PIECE', 'HALF_PACK', 'FULL_PACK', 'BOX'];

export default function SaleUnitSelector({ product, onConfirm, onCancel }) {
  const [selectedUnit, setSelectedUnit] = useState('PIECE');
  const [quantity, setQuantity] = useState(1);

  const hasPackaging = product.piecesPerPack && product.packsPerBox;
  const price = getUnitPrice(product, selectedUnit);
  const lineTotal = price * quantity;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <Package size={20} className="text-gray-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{product.productName || product.name}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">Stock: {product.stock || product.quantity || 0} pieces</p>
        </div>
      </div>

      {hasPackaging && (
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Sale Unit</label>
          <div className="grid grid-cols-2 gap-2">
            {UNITS.map((unit) => {
              const unitPrice = getUnitPrice(product, unit);
              if (!unitPrice && unit !== 'PIECE') return null;
              return (
                <button
                  key={unit}
                  type="button"
                  onClick={() => setSelectedUnit(unit)}
                  className={`px-3 py-2.5 text-sm font-medium rounded-lg border transition-all ${
                    selectedUnit === unit
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div>{getUnitLabel(unit)}</div>
                  <div className="text-xs opacity-75">Rs. {unitPrice?.toLocaleString() || '-'}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Quantity</label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            -
          </button>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            className="flex-1 text-center h-9 text-sm font-medium bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-primary-500"
          />
          <button
            type="button"
            onClick={() => setQuantity(quantity + 1)}
            className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            +
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {quantity} x {getUnitLabel(selectedUnit)}
        </span>
        <span className="text-lg font-bold text-gray-900 dark:text-white">
          Rs. {lineTotal.toLocaleString()}
        </span>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm({ unit: selectedUnit, quantity, price })}
          className="flex-1 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
}
